import * as React from 'react';
import { connect } from 'react-redux';
import { goBack, push } from 'react-router-redux';
import { assign, find, uniqBy, findIndex } from 'lodash';
import {
  approveBooking,
  getBooking,
  validateBooking,
  updateBooking,
} from '../../redux/actions/bookings';
import API from '../../redux/api';
import { getCompany, getCompanies } from '../../redux/actions/companies';
import { getCompanyDrivers } from '../../redux/actions/drivers';
import { getVehicleTypes } from '../../redux/actions/vehicleTypes';
import { getAccessories } from '../../redux/actions/accessories';
import { getSignature, removeSignature } from '../../redux/actions/signatures';
import fields from '../../data/fields';
import paymentStatuses from '../../data/paymentStatuses';

import {
  IconButton,
  Button,
  Card,
  Tag,
  Title,
  Loader,
  BookingPayment,
  BookingPrices,
  BookingCollectionValidationForm,
  BookingUpdatePricingForm,
  HeadingSection,
  RouteWrapper,
  DriverList,
  Section,
  BookingSidebar,
  Box,
} from '../../components';

type CollectionSection = 0 | 1 | 2 | 3;

interface ValidationSection {
  id?: number;
  model: 'type' | 'booking' | 'company' | 'driver' | 'drivers' | 'accessories';
  name: string;
  plural?: boolean;
}

interface Props {
  match: any;
  accessories: Array<any>;
  vehicleTypes: Array<any>;
  dispatch: Function;
}

interface State {
  accessories?: any;
  booking: any;
  companies?: any;
  editPayments: boolean;
  error: any;
  id: number;
  isApproved: boolean;
  isLoading: boolean;
  isPaid: boolean;
  isReady: boolean;
  isValidated: boolean;
  hasPaid: boolean;
  isRequesting: boolean;
  currentSection: CollectionSection;
  validationSections: ValidationSection[];
  totalOutstanding?: number;
  validationErrors: Array<any>;
  vehicleType?: any;
  validationSection: number;
  signatures: any[];
  signatureDriverIndex: number;
  companyDrivers: Array<any>;
}

class BookingCollect extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: this.props.match.params.id,
      isReady: false,
      editPayments: false,
      error: false,
      isLoading: false,
      currentSection: 0,
      booking: false,
      hasPaid: false,
      isValidated: false,
      isRequesting: false,
      validationErrors: [],
      isPaid: false,
      isApproved: false,
      validationSection: 0,
      validationSections: [] as ValidationSection[],
      signatures: [],
      signatureDriverIndex: 0,
      companyDrivers: [],
    };
  }

  /**
   * Load booking and relations from API
   */
  async componentDidMount() {
    const response = await this.props.dispatch(getBooking(this.state.id));
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }

    // Throw an error if we can't actually collect this
    if (response.data.status !== 'PENDING') {
      this.setState({ error: 'This booking has already been collected' });
      return;
    }

    // Retrieve attached resources
    const [vehicleTypes, accessories, companies] = await Promise.all([
      this.props.dispatch(getVehicleTypes()),
      this.props.dispatch(getAccessories()),
      this.fetchCompanies(response.data),
      this.fetchCompanyDrivers(response.data),
    ]);

    await this.generateValidationSections(response.data, companies);
    await this.handleValidation();

    this.setState({
      isReady: true,
      booking: response.data,
      accessories: response.data.accessories,
      totalOutstanding: Number(
        (response.data.total_cost - response.data.total_paid).toFixed(2),
      ),
      vehicleType: find(this.props.vehicleTypes, {
        id: response.data.vehicle_type_id,
      }),
    });

    await this.fetchSignatures();
  }

  private fetchCompanies = async (booking = this.state.booking) => {
    console.log('Fetching companies...');
    this.setState({ isLoading: true });
    // Get the ids of all the companies attached to the drivers
    let companies = booking.drivers.map((driver) => Number(driver.company_id));

    // Add the booking's company to the start
    companies.unshift(Number(booking.company_id));

    // Remove undefined companies and duplicates
    companies = companies.filter((company) => company);
    companies = uniqBy(companies, (company) => company);

    companies = await Promise.all(
      companies.map(async (company) => {
        return (await this.props.dispatch(getCompany(company))).data;
      }),
    );

    this.setState({ companies });

    return companies;
  };

  private fetchCompanyDrivers = async (booking = this.state.booking) => {
    console.log('Fetching company drivers...');
    this.setState({ isLoading: true });
    // Get the company drivers
    const companyDrivers = (await this.props.dispatch(
      getCompanyDrivers(booking.company_id, 0),
    )).data;
    this.setState({ companyDrivers });
    return companyDrivers;
  };

  private async fetchSignatures() {
    console.log('Fetching signatures...');
    this.setState({ isLoading: true });
    const signatures = this.state.signatures;
    console.log(signatures)
    await Promise.all(
      this.state.booking.drivers.map(async (driver) => {
        const response = await this.props.dispatch(
          getSignature(this.state.booking.id, driver.id),
        );
        if (!response.error) {
          if (!find(signatures, { driver_id: driver.id })) {
            signatures.push({
              driver_id: driver.id,
              src: response.data,
            });
          }
        }
      }),
    );
    this.setState({
      signatures,
      isLoading: false,
    });
  }

  private generateValidationSections = async (
    booking = this.state.booking,
    companies = this.state.companies,
  ) => {
    console.log('Generating validation sections...');
    const contactCreationSection: ValidationSection = {
      model: 'type',
      name: 'Type',
      plural: true,
    };

    const driverSelectionSection: ValidationSection = {
      model: 'drivers',
      name: 'Additional Drivers',
      plural: true,
    };

    const accessoriesSelectionSection: ValidationSection = {
      model: 'accessories',
      name: 'Accessories',
      plural: true,
    };

    const bookingSection: ValidationSection = {
      id: this.state.booking.id,
      model: 'booking',
      name: 'Booking',
    };

    // Get all companies for companies form
    await this.props.dispatch(getCompanies());

    // Remap to section interface structure
    const companiesSection = companies.map((company) => ({
      id: company.id,
      model: 'company',
      name: company.name,
    }));

    const driversSection = booking.drivers.map((driver) => ({
      id: driver.id,
      model: 'driver',
      name: driver.name,
    }));

    const [firstDriver, ...restOfDrivers] = driversSection;
    const [firstCompany, ...restOfCompanies] = companiesSection;

    let contactSection;
    if (
      booking.company_id !== null &&
      !isNaN(parseInt(booking.company_id, 10))
    ) {
      contactSection = [firstCompany];
    } else {
      contactSection = firstDriver ? [firstDriver] : [];
    }

    const validationSections = [
      contactCreationSection,
      ...contactSection,
      driverSelectionSection,
      ...(booking.company_id ? driversSection : restOfDrivers),
      ...(booking.company_id ? restOfCompanies : companiesSection),
      accessoriesSelectionSection,
      bookingSection,
    ] as Array<ValidationSection>;

    let validationSection = this.state.validationSection;
    if (validationSection === 0 && contactSection.length) {
      validationSection++;
    }

    this.setState({
      validationSection,
      validationSections,
      isLoading: false,
    });
  };

  /**
   * Update the payment status using a response from the server
   * @param {Object} response JSON response
   */
  private handleUpdate = async (response) => {
    console.log('Updating...');
    let updateState: any = {
      error: response.error,
    };
    if (!response.error) {
      updateState = {
        booking: response.data,
        isPaid:
          response.data.total_paid === response.data.total_cost ||
          response.data.payment_status > 0,
        error: response.error,
        id: response.data.id,
        isApproved: response.data.status !== 'PENDING',
        isLoading: false,
        totalOutstanding: Number(
          (response.data.total_cost - response.data.total_paid).toFixed(2),
        ),
      };
      if (response.data.company_id !== null) {
        updateState.company = (await this.props.dispatch(
          getCompany(response.data.company_id),
        )).data;
      }
      this.setState(updateState);
    }
    return updateState;
  };

  /**
   * Handle booking validation
   */
  private handleValidation = async (id: number = this.state.id) => {
    console.log('Validating...');
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true, isValidated: false });
    const response = await this.props.dispatch(validateBooking(id));
    if (response.error !== false) {
      this.setState({ error: response.error });
      return;
    }
    this.setState({
      isLoading: false,
      isValidated: response.data === true,
      validationErrors: response.data !== true ? response.data : [],
    });
  };

  /**
   * Render the validation section
   * This will trigger validation for every field and return any errors
   */
  private renderValidationSection = () => {
    if (!this.state.validationSections) {
      return null;
    }
    return (
      <BookingCollectionValidationForm
        key={this.state.booking.updated_at}
        booking={this.state.booking}
        companies={this.state.companies}
        section={this.state.validationSections[this.state.validationSection]}
        vehicleTypes={this.props.vehicleTypes}
        drivers={this.state.booking.drivers}
        companyDrivers={this.state.companyDrivers}
        errors={this.state.validationErrors}
        handleUpdate={this.handleUpdate}
        handleValidation={this.handleValidation}
        isValid={this.state.isValidated}
        updateBooking={(booking) => {
          this.setState({
            booking: assign({}, booking, {
              drivers: [...booking.drivers],
            }),
          });
        }}
        previousSection={() => {
          this.setState({
            validationSection: this.state.validationSection - 1,
          });
        }}
        nextSection={async () => {
          const validationSection = this.state.validationSection + 1;

          await this.fetchCompanies();
          await this.fetchCompanyDrivers();
          await this.generateValidationSections();
          this.setState({
            validationSection,
            isPaid:
              this.state.booking.total_cost === this.state.booking.total_paid ||
              this.state.booking.payment_status > 0,
            currentSection:
              validationSection >= this.state.validationSections.length ? 1 : 0,
          });
        }}
      />
    );
  };

  /**
   * Render the payment section
   * This will ensure all payment has been met before the hire begins
   */
  private renderPaymentSection = () => {
    const { booking, totalOutstanding, vehicleType } = this.state;

    // Determine payment status
    let paymentStatus: any = paymentStatuses[0];
    if (booking.payment_status) {
      paymentStatus = find(paymentStatuses, { value: booking.payment_status });
    }

    return (
      <>
        <div className="column is-8-desktop is-7-widescreen">
          <Box>
            <div className="is-pulled-right">
              <Tag type={paymentStatus.class}>
                {paymentStatus.name.toUpperCase()}
              </Tag>
              &nbsp;
              <IconButton
                icon={this.state.editPayments ? 'undo' : 'pencil'}
                className="is-default is-small"
                handleClick={() =>
                  this.setState({ editPayments: !this.state.editPayments })
                }
              >
                {this.state.editPayments ? 'Cancel' : 'Edit'}
              </IconButton>
            </div>
            <h4 className="heading">Payment Finalisation</h4>
            {!this.state.editPayments ? (
              <div>
                <div className="field">
                  <BookingPrices booking={booking} vehicleType={vehicleType} />
                </div>
                <div className="field">
                  <BookingPayment
                    booking={booking}
                    handleUpdate={(response) => {
                      this.setState({ isLoading: true }, async () => {
                        await this.handleUpdate(response);
                        const state: any = {
                          isLoading: false,
                          hasPaid: true,
                        };
                        this.setState(state);
                      });
                    }}
                  />
                </div>
                {(totalOutstanding <= 0 ||
                  booking.payment_status !== 0 ||
                  (this.state.hasPaid && totalOutstanding > 0)) && (
                  <div className="field">
                    <div className="has-text-right">
                      <IconButton
                        icon="arrow-right"
                        className="is-medium"
                        isRight
                        handleClick={async () => {
                          if (this.state.booking.can_sign) {
                            this.fetchSignatures();
                            this.setState({ currentSection: 2 });
                            return;
                          }
                          if (this.state.isLoading) {
                            return;
                          }
                          this.setState({ isLoading: true });
                          const { error, data } = await this.props.dispatch(
                            updateBooking(this.state.id, { can_sign: true }),
                          );
                          if (!error) {
                            this.fetchSignatures();
                            this.setState({
                              isLoading: false,
                              booking: data,
                              id: data.id,
                              currentSection: 2,
                            });
                          }
                        }}
                      >
                        Continue
                      </IconButton>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <BookingUpdatePricingForm
                fields={fields.bookings.updateCollectionPricing}
                booking={this.state.booking}
                handleUpdate={async (pricing) => {
                  if (this.state.isRequesting) {
                    return;
                  }
                  this.setState({
                    isRequesting: true,
                  });
                  const { error, data } = await this.props.dispatch(
                    updateBooking(this.state.id, pricing),
                  );
                  if (!error) {
                    this.setState({
                      editPayments: false,
                      isRequesting: false,
                      booking: data,
                      id: data.id,
                    });
                  } else {
                    this.setState({
                      error,
                    });
                  }
                }}
              />
            )}
          </Box>
        </div>
        <div className="column is-4-desktop is-5-widescreen">
          <BookingSidebar booking={this.state.booking} />
        </div>
      </>
    );
  };

  /**
   * Render the signature section
   * Every driver attached must have provided a signature
   */
  private renderApprovalSection = () => {
    if (!this.state.booking) {
      return null;
    }
    const index = this.state.signatureDriverIndex;
    const driver = this.state.booking.drivers[index];
    const signature = find(this.state.signatures, {
      driver_id: driver.id,
    });
    return (
      <>
        <div className="column is-8-desktop is-7-widescreen">
          <Card>
            <div className="field">
              {signature ? (
                <div className="has-text-centered">
                  <p>
                    Signature for <strong>{driver.name}</strong>:
                  </p>
                  <img src={signature.src} alt="Signature" />
                </div>
              ) : (
                <div className="has-text-centered">
                  <p>
                    Booking code for{' '}
                    <strong>{this.state.booking.invoiceId}</strong>:
                  </p>
                  <Title>{this.state.booking.id}</Title>
                  <p>
                    Driver code for <strong>{driver.name}</strong>:
                  </p>
                  <Title>{driver.id}</Title>
                  <p>
                    Please enter these details into the signature section and
                    await the driver's signature.
                  </p>
                </div>
              )}
            </div>
            {this.state.isLoading ? (
              <Loader fullwidth />
            ) : (
              <div className="buttons is-centered">
                {!this.state.isApproved && !!signature ? (
                  <IconButton
                    icon="pencil"
                    className="is-default"
                    handleClick={async () => {
                      if (!this.state.isLoading) {
                        this.setState({ isLoading: true });
                        await this.props.dispatch(
                          removeSignature(this.state.booking.id, driver.id),
                        );
                        const response = await this.props.dispatch(
                          updateBooking(this.state.id, {
                            can_sign: true,
                            is_signed: false,
                          }),
                        );
                        if (!response.error) {
                          const signatures = this.state.signatures;
                          const signatureIndex = findIndex(signatures, {
                            driver_id: driver.id,
                          });
                          if (signatureIndex !== -1) {
                            signatures.splice(signatureIndex, 1);
                          }
                          this.setState({
                            signatures,
                            isLoading: false,
                            booking: response.data,
                            id: response.data.id,
                          });
                        } else {
                          this.setState({
                            isLoading: false,
                          });
                        }
                      }
                    }}
                  >
                    Sign Again
                  </IconButton>
                ) : (
                  <IconButton
                    icon="refresh"
                    className="is-default"
                    handleClick={async () => {
                      if (!this.state.isLoading) {
                        this.fetchSignatures();
                      }
                    }}
                  >
                    Check Signature
                  </IconButton>
                )}
                {this.state.signatures.length ===
                  this.state.booking.drivers.length && (
                  <IconButton
                    icon="check-circle"
                    handleClick={async () => {
                      if (!this.state.isLoading) {
                        this.setState({ isLoading: true });
                        const response = await this.props.dispatch(
                          approveBooking(this.state.id),
                        );
                        if (response.error) {
                          this.setState({ error: response.error });
                          return;
                        }
                        this.handleUpdate(response);
                        this.setState({ currentSection: 3 }, () => {
                          this.props.dispatch(push('/dashboard'));
                        });
                      }
                    }}
                  >
                    Approve for Collection
                  </IconButton>
                )}
              </div>
            )}
          </Card>
        </div>
        <div className="column is-4-desktop is-5-widescreen">
          <DriverList
            items={this.state.booking.drivers}
            showJoinDate={false}
            showReputation={true}
            handleClick={(driver, index) => {
              this.setState({
                signatureDriverIndex: index,
              });
            }}
            isSelected={(id) => driver.id === id}
            hideSearch
          />
          <div className="buttons">
            <IconButton
              icon="file"
              className="is-default is-fullwidth"
              handleClick={() => {
                window.open(
                  `${API.getURL()}/bookings/${
                    this.state.id
                  }/pdf/hire-agreement?${API.authParams}`,
                );
              }}
            >
              View Hire Agreement
            </IconButton>
          </div>
          <BookingSidebar booking={this.state.booking} />
        </div>
      </>
    );
  };

  private renderValidationTabs = () =>
    this.state.validationSections.map((section, index) => {
      if (this.state.validationSection > 0 && index === 0) {
        return null;
      }

      const isLead =
        index === 1 && ['driver', 'company'].indexOf(section.model) !== -1;
      const isPast = index < this.state.validationSection;
      const isCurrent = index === this.state.validationSection;

      return (
        <Button
          key={index}
          className={`${isCurrent || isPast ? 'is-primary' : 'is-static'} ${
            isPast ? 'is-outlined' : ''
          }`}
          handleClick={() => {
            if (isPast) {
              this.setState({ validationSection: index, currentSection: 0 });
            }
          }}
        >
          {isLead ? (
            <span style={{ textTransform: 'capitalize' }}>
              Lead {section.model}
            </span>
          ) : (
            section.name
          )}
        </Button>
      );
    });

  private renderTabs = () => (
    <>
      {!this.state.isReady && this.state.validationSections.length === 0 && (
        <Loader fullwidth />
      )}
      <div className="button-group field is-grouped wrap-line">
        {this.renderValidationTabs()}
        {['Payment', 'Signature'].map((name, index) => {
          const isPast = index + 1 < this.state.currentSection;
          const isCurrent = index + 1 === this.state.currentSection;
          return (
            <Button
              key={name}
              className={`${isCurrent || isPast ? 'is-primary' : 'is-static'} ${
                isPast ? 'is-outlined' : ''
              }`}
              handleClick={() => {
                if (this.state.currentSection > index + 1) {
                  this.setState({ currentSection: (index + 1) as any });
                }
              }}
            >
              {name}
            </Button>
          );
        })}
      </div>
    </>
  );

  render() {
    const renderSection = () => {
      switch (this.state.currentSection) {
        case 0:
          return this.renderValidationSection();
        case 1:
          return this.renderPaymentSection();
        case 2:
        case 3:
          return this.renderApprovalSection();
        default:
          return null;
      }
    };
    return (
      <RouteWrapper
        title="Collect Booking"
        requireAuth
        requirePermission="MANAGE_BOOKINGS"
        breadcrumbs={[
          {
            link: `/bookings/status/${this.state.booking.status}`,
            text: `Bookings`,
          },
          {
            link: `/bookings/${this.state.id}`,
            text: `${this.state.booking.invoiceId}`,
          },
          { link: `/bookings/${this.state.id}/collect`, text: 'Collect' },
        ]}
        error={this.state.error}
        ready={this.state.isReady}
        loading={this.state.isLoading}
      >
        <HeadingSection
          title="Collect Booking"
          controls={[
            <IconButton
              key="return"
              icon="undo"
              className="is-default is-pulled-right"
              handleClick={() => {
                this.props.dispatch(goBack());
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <div className="columns">
            <div className="column">{this.renderTabs()}</div>
          </div>
          <div className="columns">{renderSection()}</div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ accessories, vehicleTypes }) => ({
  accessories: accessories.accessories,
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(BookingCollect);
